import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNotification } from '../common';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

const ServicesSettings = () => {
  // Import useNotification hook
  const { showNotification } = useNotification();
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  // Initialize form data with service_type for new services
  const [formData, setFormData] = useState({
    name: '',
    service_type: 'education', // Default type
    level: '',
    age_group: '',
    course_format: '',
    description: '',
    is_active: true,
    max_students: 10,
    image_data: '' // Add field for image data
  });
  
  // State for image preview
  const [imagePreview, setImagePreview] = useState('');
  // Reference to file input
  const fileInputRef = useRef(null);
  
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    serviceId: null,
    serviceName: ''
  });
  
  // Education-specific options
  const ageGroups = ['8-10', '11-13'];
  const levels = ['1', '2', '3'];
  const courseFormats = ['Live', 'Video', '1-to-1'];
  
  // Service type options
  const serviceTypes = [
    { value: 'education', label: 'Educational Course' },
    { value: 'consulting', label: 'Consulting Service' },
    { value: 'product', label: 'Product' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'other', label: 'Other Service' }
  ];
  
  // Fetch services (using the services endpoint for future compatibility)
  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Use the courses endpoint directly for image support
      const response = await axios.get('/api/settings/courses', config);
      setServices(response.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchServices();
  }, []);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.type, file.size);
      
      // Create preview URL for the image
      setImagePreview(URL.createObjectURL(file));
      
      // Store the file for later upload
      // We'll upload it separately from the form submission
      const reader = new FileReader();
      reader.onload = () => {
        console.log('File read as data URL, length:', reader.result.length);
        // Just store the image data now, we'll use it when form is submitted
        setFormData(prevData => ({
          ...prevData,
          has_new_image: true,
          image_data: reader.result,
          image_name: file.name,
          image_type: file.type,
          image_size: file.size
        }));
      };
      reader.onerror = () => {
        console.error('Error reading file');
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle image removal
  const handleRemoveImage = async () => {
    // If editing an existing service and it has an image
    if (editingService && editingService.image) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found');
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Delete the image on the server - use courses endpoint directly
        await axios.delete(`/api/settings/courses/${editingService.id}/image`, config);
        showNotification('Image removed successfully', 'success');
      } catch (err) {
        console.error('Error removing image:', err);
        showNotification('Failed to remove image', 'error');
      }
    }
    
    // Clear the image in the form and preview
    setFormData({
      ...formData,
      image_data: ''
    });
    setImagePreview('');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleAddNew = () => {
    setFormData({
      name: '',
      service_type: 'education', // Default to education
      level: '',
      age_group: '',
      course_format: '',
      description: '',
      is_active: true,
      max_students: 10,
      image_data: '' // Reset image data
    });
    // Clear image preview
    setImagePreview('');
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setEditingService(null);
    setShowForm(true);
  };
  
  const handleEdit = (service) => {
    setFormData({
      name: service.name,
      service_type: service.service_type || 'education', // Default to education for backward compatibility
      level: service.level || '',
      age_group: service.age_group || '',
      course_format: service.course_format || '',
      description: service.description || '',
      is_active: service.is_active,
      max_students: service.max_students || 10,
      image_data: '' // Reset image data when editing
    });
    
    // If the service has an image, set it as the preview
    if (service.image) {
      setImagePreview(service.image);
    } else {
      setImagePreview('');
    }
    
    setEditingService(service);
    setShowForm(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      // Log what we're submitting
      console.log('Submitting form with image_data length:', 
        formData.image_data ? formData.image_data.length : 0);
      console.log('Image metadata being submitted:', {
        name: formData.image_name,
        type: formData.image_type,
        size: formData.image_size
      });
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Validate required fields based on service type
      if (!formData.name || !formData.service_type) {
        setError('Name and Service Type are required');
        return;
      }
      
      // Only validate education-specific fields if the service type is education
      if (formData.service_type === 'education' && 
          (!formData.level || !formData.age_group || !formData.course_format)) {
        setError('For educational services, Level, Age Group, and Format are required');
        return;
      }
      
      // Remove image data from the form submission to make the payload smaller
      const formDataWithoutImage = { ...formData };
      delete formDataWithoutImage.image_data;
      delete formDataWithoutImage.has_new_image;
      delete formDataWithoutImage.image_name;
      delete formDataWithoutImage.image_type;
      delete formDataWithoutImage.image_size;
      
      let serviceId;
      
      if (editingService) {
        // Update existing service - use courses endpoint directly
        const updateResponse = await axios.put(
          `/api/settings/courses/${editingService.id}`,
          formDataWithoutImage,
          config
        );
        serviceId = editingService.id;
      } else {
        // Create new service - use courses endpoint directly
        const createResponse = await axios.post('/api/settings/courses', formDataWithoutImage, config);
        serviceId = createResponse.data.id;
      }
      
      // If we have an image to upload, do it separately
      if (formData.has_new_image && formData.image_data) {
        console.log('Uploading image via dedicated endpoint for service ID:', serviceId);
        await axios.post(
          `/api/settings/courses/${serviceId}/image-base64`,
          {
            data: formData.image_data,
            filename: formData.image_name || 'image.png',
            mimeType: formData.image_type || 'image/png',
            size: formData.image_size || 0
          },
          config
        );
      }
      
      // Refresh services list
      fetchServices();
      
      // Reset form
      setFormData({
        name: '',
        service_type: 'education',
        level: '',
        age_group: '',
        course_format: '',
        description: '',
        is_active: true,
        max_students: 10,
        image_data: ''
      });
      // Clear image preview
      setImagePreview('');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setEditingService(null);
      setShowForm(false);
    } catch (err) {
      console.error('Error saving service:', err);
      setError('Failed to save service: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const handleDelete = async (serviceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      try {
        // Try services endpoint first
        await axios.delete(`/api/settings/services/${serviceId}`, config);
      } catch (err) {
        // Fall back to courses endpoint
        await axios.delete(`/api/settings/courses/${serviceId}`, config);
      }
      
      // Reset delete modal
      setDeleteModal({
        isOpen: false,
        serviceId: null,
        serviceName: ''
      });
      
      // Refresh services list
      fetchServices();
      showNotification('Service deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting service:', err);
      showNotification('Failed to delete service: ' + (err.response?.data?.message || err.message), 'error');
    }
  };
  
  const openDeleteModal = (service) => {
    setDeleteModal({
      isOpen: true,
      serviceId: service.id,
      serviceName: service.name
    });
  };
  
  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      serviceId: null,
      serviceName: ''
    });
  };
  
  // Get service type label
  const getServiceTypeLabel = (type) => {
    const typeObj = serviceTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };
  
  if (loading) {
    return <div className="text-center p-4">Loading services...</div>;
  }
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Service Offerings</h2>
        <button
          onClick={handleAddNew}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          Add New Service
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {showForm && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Service Type
              </label>
              <select
                name="service_type"
                value={formData.service_type}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {serviceTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the type of service you are offering
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Service Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter service name"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Education-specific fields, only shown for educational services */}
            {formData.service_type === 'education' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Age Group
                    </label>
                    <select
                      name="age_group"
                      value={formData.age_group}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    >
                      <option value="">Select Age Group</option>
                      {ageGroups.map(group => (
                        <option key={group} value={group}>{group} years</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Level
                    </label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    >
                      <option value="">Select Level</option>
                      {levels.map(level => (
                        <option key={level} value={level}>Level {level}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Course Format
                  </label>
                  <select
                    name="course_format"
                    value={formData.course_format}
                    onChange={handleInputChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  >
                    <option value="">Select Format</option>
                    {courseFormats.map(format => (
                      <option key={format} value={format}>{format}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the service"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                rows="3"
              />
            </div>
            
            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Service Image
              </label>
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload an image to represent this service. Recommended size: 800x600px.
                  </p>
                </div>
                
                {/* Image Preview */}
                {(imagePreview || (editingService && editingService.image)) && (
                  <div className="w-32">
                    <div className="relative">
                      <img
                        src={imagePreview || editingService.image}
                        alt="Service preview"
                        className="w-full h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Only show max_students for educational services */}
            {formData.service_type === 'education' && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Max Participants
                </label>
                <input
                  type="number"
                  name="max_students"
                  value={formData.max_students}
                  onChange={handleInputChange}
                  min="1"
                  max="500"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum number of participants that can be enrolled
                </p>
              </div>
            )}
            
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="mr-2 h-4 w-4"
              />
              <label htmlFor="is_active" className="text-gray-700">
                Active (available for selection in deals)
              </label>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingService(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                {editingService ? 'Update Service' : 'Add Service'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {services.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No services found. Click "Add New Service" to create your first service offering.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                {/* Show education-specific columns but handle empty values */}
                {/* Only show education-specific columns if there are education services */}
                {services.some(service => service.service_type === 'education' || !service.service_type) && (
                  <>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Age Group
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Format
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max Participants
                    </th>
                  </>
                )}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {services.map(service => (
                <tr key={service.id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {service.image && (
                        <div className="flex-shrink-0 h-10 w-10 mr-3">
                          <img 
                            className="h-10 w-10 rounded-sm object-cover" 
                            src={service.image} 
                            alt={service.name} 
                          />
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{service.name}</div>
                        {service.description && (
                          <div className="text-xs text-gray-500 mt-1">{service.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {getServiceTypeLabel(service.service_type || 'education')}
                    </div>
                  </td>
                  {/* Only display education-specific columns if any service has education type */}
                  {services.some(s => s.service_type === 'education' || !s.service_type) && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {(service.service_type === 'education' || !service.service_type) && service.age_group 
                            ? `${service.age_group} years` 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {(service.service_type === 'education' || !service.service_type) && service.level 
                            ? `Level ${service.level}` 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {(service.service_type === 'education' || !service.service_type) 
                            ? service.course_format || '-' 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {(service.service_type === 'education' || !service.service_type) 
                            ? service.max_students || '-' 
                            : '-'}
                        </div>
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      service.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(service)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteModal(service)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => handleDelete(deleteModal.serviceId)}
        title="Delete Service"
        message={`Are you sure you want to delete the service "${deleteModal.serviceName}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default ServicesSettings;